import dayjs from 'dayjs'
import { capitalize } from '@/helpers/string'
import { useElapsedTime } from '@/helpers/time'
import { StorageDeleteDialog } from './DeleteStorageDialog'
import { EditStorageDialog } from './EditStorageDialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Database, HardDrive, Server } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ListStorageVolumeDataItem } from '@/services/inference.gen'
import { Button } from '@/components/ui/button'
import { ServerMountDetails } from './ServerMountDetails'
import { GoogleDriveDetails } from './GoogleDriveDetails'
import { UptimeBadge } from '@/components/UptimeBadge'
import GoogleDriveLogo from '/assets/image/google-drive.svg'
import { Progress } from '@/components/ui/progress'
import { useSyncStorageGoogleDrive } from '@/services/storage'
import { RotateCw } from 'lucide-react'
import { getStorageTypeById, StorageTypeId } from '@/constants/storageTypes'
import { StorageMigrationStatus } from '@/components/StorageMigrationStatus'

type StorageCardProps = ListStorageVolumeDataItem & {
  allVolumes?: ListStorageVolumeDataItem[]
}

export const StorageCard = ({
  storage_volume,
  user_metadata,
  server_instances,
  allVolumes = [],
}: StorageCardProps) => {
  const uptime = useElapsedTime(storage_volume.created_at || '')
  const isMounted = storage_volume.status === 'mounted'
  const hasGoogleDriveSync = !!storage_volume.google_drive_metadata?.length

  const googleDriveMetadata = storage_volume.google_drive_metadata?.[0]
  const syncStatus = googleDriveMetadata?.status || ''
  const isSyncing =
    hasGoogleDriveSync && syncStatus !== 'success' && syncStatus !== 'failed'
  const syncProgress = googleDriveMetadata?.progress || 0

  const syncMutation = useSyncStorageGoogleDrive()

  const handleDeleteSuccess = () => {
    console.log(`Volume ${storage_volume.volume_name} deleted successfully`)
  }

  const handleSyncClick = () => {
    syncMutation.mutate(storage_volume.volume_id)
  }

  const getSyncButtonText = () => {
    if (syncMutation.isPending) return 'Syncing...'
    if (syncStatus === 'failed') return 'Retry Sync'
    if (syncStatus === 'success') return 'Refresh Drive'
    return 'Sync Now'
  }

  const isSyncButtonDisabled = isSyncing || syncMutation.isPending

  const getStorageIcon = () => {
    if (storage_volume.type_id === StorageTypeId.LONGHORN) {
      return <HardDrive className="w-5 h-5 text-primary/70" />
    }
    if (storage_volume.type_id === StorageTypeId.LOCAL) {
      return <Server className="w-5 h-5 text-primary/70" />
    }
    return <Database className="w-5 h-5 text-primary/70" />
  }

  return (
    <div
      className={cn(
        'storage-card',
        'p-6 rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md group',
        'bg-card border-border',
        isMounted && 'border-primary border-2'
      )}
    >
      <div className="flex justify-between items-start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {getStorageIcon()}
            <h3 className="text-2xl font-bold">{storage_volume.volume_name}</h3>
            {storage_volume.is_public && (
              <Badge variant="secondary" className="ml-2">
                Public
              </Badge>
            )}
          </div>

          <UptimeBadge
            status={capitalize(storage_volume.status)}
            uptime={uptime}
            variant={isMounted ? 'running' : 'default'}
          />
        </div>

        <div className="text-right space-y-1">
          <p className="text-lg font-semibold">{storage_volume.size_gb} GB</p>
          <p className="text-foreground/60">Total Volume Size</p>
        </div>
      </div>

      <Separator className="my-4" />

      {/* Show migration status for local storage with backup/restore jobs or migrated volumes */}
      {storage_volume.type_id === StorageTypeId.LOCAL &&
        storage_volume.storage_volume_kube?.[0] &&
        (storage_volume.storage_volume_kube[0].storage_volume_backups?.length ||
          storage_volume.storage_volume_kube[0].storage_volume_restores?.length ||
          storage_volume.origin_storage_volume_id) && (
          <div className="mb-4">
            <StorageMigrationStatus
              storageVolume={storage_volume}
              storageVolumeKube={storage_volume.storage_volume_kube[0]}
            />
          </div>
        )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-2 space-y-2 text-sm">
          <InfoField label="Volume ID" value={storage_volume.volume_id} />

          <InfoField
            label="Storage Type"
            value={
              storage_volume.storage_volume_type?.name?.toUpperCase() ||
              getStorageTypeById(storage_volume.type_id)?.label ||
              'Unknown'
            }
          />

          <InfoField
            label="Created By"
            value={
              <>
                {storage_volume.user?.email}
                {user_metadata &&
                  user_metadata.last_name &&
                  ` (${user_metadata.first_name} ${user_metadata.last_name})`}
              </>
            }
          />

          <InfoField
            label="Created On"
            value={
              storage_volume.created_at
                ? dayjs(storage_volume.created_at).format('MMMM DD, YYYY')
                : ''
            }
          />

          {storage_volume.origin_storage_volume_id && (
            <InfoField
              label="Migrated From"
              value={
                allVolumes.find(
                  (v) =>
                    v.storage_volume.volume_id ===
                    storage_volume.origin_storage_volume_id
                )?.storage_volume.volume_name ||
                `Volume #${storage_volume.origin_storage_volume_id}`
              }
            />
          )}

          {hasGoogleDriveSync && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-foreground/60 w-24">Synced with:</span>
              <div className="flex items-center gap-2">
                <GoogleDriveDetails
                  googleDriveMetadata={
                    storage_volume.google_drive_metadata || []
                  }
                  TriggerButton={
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5"
                    >
                      <img
                        src={GoogleDriveLogo}
                        alt="Google Drive"
                        className="w-4 h-4"
                      />
                      Google Drive
                    </Button>
                  }
                />
                {isSyncing && (
                  <div className="ml-2 flex items-center gap-1.5">
                    <Badge variant="secondary" className="h-6 px-2 text-xs">
                      {Math.round(syncProgress)}% syncing
                    </Badge>
                    <div className="w-16">
                      <Progress value={syncProgress} className="h-1" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col justify-end gap-2">
          <div className="flex items-center gap-2 justify-end mt-auto">
            <ServerMountDetails
              serversMounted={server_instances}
              TriggerButton={
                <Button size="default" variant="outline">
                  {server_instances.length > 0
                    ? `${server_instances.length} Server${server_instances.length !== 1 ? 's' : ''}`
                    : 'Not Mounted'}
                </Button>
              }
            />
            {hasGoogleDriveSync && (
              <Button
                size="default"
                variant={syncStatus === 'failed' ? 'destructive' : 'outline'}
                onClick={handleSyncClick}
                disabled={isSyncButtonDisabled}
                className="flex items-center gap-1.5"
              >
                <RotateCw
                  className={cn(
                    'w-4 h-4',
                    (syncMutation.isPending || isSyncing) && 'animate-spin'
                  )}
                />
                {getSyncButtonText()}
              </Button>
            )}
            <EditStorageDialog
              volumeId={storage_volume.volume_id}
              volumeName={storage_volume.volume_name}
              isPublic={storage_volume.is_public}
            />
            <StorageDeleteDialog
              id={String(storage_volume.volume_id)}
              name={storage_volume.volume_name}
              status={storage_volume.status}
              onDeleteSuccess={handleDeleteSuccess}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

type InfoFieldProps = {
  label: string
  value: React.ReactNode
}

const InfoField = ({ label, value }: InfoFieldProps) => (
  <div className="flex gap-2">
    <span className="text-foreground/60 w-24">{label}:</span>
    <span className="font-medium">{value}</span>
  </div>
)
